// src/pages/DocumentUploadPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { getServiceById, uploadDocument } from '../services/api';
import { UploadCloud, File, FileText } from 'lucide-react';

import DocumentViewer from '../components/DocumentViewer';

function DocumentUploadPage() {
    const { serviceId } = useParams();
    const [searchParams] = useSearchParams();
    const caseId = searchParams.get('caseId');
    const navigate = useNavigate();

    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // --- MODIFIED STATE FOR MULTIPLE DOCUMENTS ---
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [finalDocuments, setFinalDocuments] = useState([]);
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
    // -------------------------------------------

    const [selectedFiles, setSelectedFiles] = useState({});

    useEffect(() => {
        console.log("[DEBUG] DocumentUploadPage - serviceId:", serviceId, "caseId:", caseId);
        if (!caseId) {
            setError("Case ID is missing. Cannot proceed.");
            setLoading(false);
            return;
        }
        if (serviceId) {
            setLoading(true);
            setError(null);
            console.log("[DEBUG] DocumentUploadPage - Calling getServiceById for serviceId:", serviceId);
            getServiceById(serviceId)
                .then(response => {
                    console.log("[DEBUG] DocumentUploadPage - getServiceById response:", response.data);
                    setService(response.data);

                    // --- NEW: Store all output documents ---
                    const outputDocs = response.data?.outputDocuments || [];
                    console.log("[DEBUG] DocumentUploadPage - finalDocuments:", outputDocs);
                    setFinalDocuments(outputDocs);
                    // -----------------------------------------

                    const initialFiles = {};
                    (response.data?.eligibilityDocuments || []).forEach(docName => {
                        initialFiles[docName] = null;
                    });
                    setSelectedFiles(initialFiles);
                })
                .catch(err => {
                    console.error("[DEBUG] DocumentUploadPage - Error fetching service details:", err);
                    setError(err.response?.data?.message || 'Failed to load service requirements.');
                })
                .finally(() => {
                    console.log("[DEBUG] DocumentUploadPage - getServiceById call finished.");
                    setLoading(false);
                });
        } else {
            setError("Service ID is missing.");
            setLoading(false);
        }
    }, [serviceId, caseId]);

    const handleFileChange = (event, documentName) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFiles(prev => ({ ...prev, [documentName]: file }));
        } else {
            setSelectedFiles(prev => ({ ...prev, [documentName]: null }));
        }
    };

    const handleSubmitDocuments = async () => {
        setSubmitError(null);
        const requiredDocs = service?.eligibilityDocuments || [];
        const missingDocs = requiredDocs.filter(docName => !selectedFiles[docName]);

        if (missingDocs.length > 0) {
            setSubmitError(`Please select files for all required documents: ${missingDocs.join(', ')}`);
            return;
        }

        setIsSubmitting(true);
        try {
            const uploadPromises = Object.entries(selectedFiles).map(([docName, file]) => {
                if (!file) return Promise.resolve({ docName, status: 'skipped' });
                return uploadDocument(caseId, file, docName);
            });
            await Promise.all(uploadPromises);
            setSubmitSuccess(true);
        } catch (err) {
            console.error("Error uploading documents:", err);
            setSubmitError(err.response?.data?.message || err.message || 'An unexpected error occurred during upload.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- NEW: Handlers for multi-document viewing ---
    const handleViewFinalDocuments = () => {
        if (finalDocuments.length > 0) {
            setCurrentDocumentIndex(0); // Start from the first document
            setIsViewerOpen(true);
        } else {
            handleFinishApplication(); // If no docs, just finish
        }
    };

    const handleNextDocument = () => {
        console.log("[DEBUG] DocumentUploadPage - handleNextDocument called. Current index:", currentDocumentIndex);
        if (currentDocumentIndex < finalDocuments.length - 1) {
            setCurrentDocumentIndex(prevIndex => prevIndex + 1);
            console.log("[DEBUG] DocumentUploadPage - Advancing to next document. New index:", currentDocumentIndex + 1);
        } else {
            console.log("[DEBUG] DocumentUploadPage - No more documents to advance to.");
        }
    };

    const handleFinishApplication = useCallback(() => {
        setIsViewerOpen(false);
        navigate('/');
    }, [navigate]);
    // -------------------------------------------------

    if (loading) return <p className="loading-message">Loading document requirements...</p>;
    if (error) return <p className="error-message">Error: {error}</p>;
    if (!service) return <p>Service details not found.</p>;

    const requiredDocuments = service.eligibilityDocuments || [];
    const allFilesSelected = requiredDocuments.every(docName => selectedFiles[docName]);
    const hasFinalDocuments = finalDocuments.length > 0;

    const currentDocument = finalDocuments[currentDocumentIndex];

    return (
        <div className="document-upload-container">
            <h1>Upload Required Documents</h1>
            <p>Please upload the following documents for: <strong>{service.name}</strong></p>

            {submitError && <p className="error-message api-error">{submitError}</p>}

            {!submitSuccess ? (
                <div className="document-list">
                    {requiredDocuments.length > 0 ? (
                        requiredDocuments.map((docName) => (
                            <div key={docName} className="document-upload-item form-group">
                                <label htmlFor={`file-${docName}`}>
                                    <File size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                    {docName}:*
                                </label>
                                <div className="file-input-wrapper">
                                    <input
                                        type="file"
                                        id={`file-${docName}`}
                                        onChange={(e) => handleFileChange(e, docName)}
                                    />
                                    {selectedFiles[docName] && (
                                        <span className="selected-file-name">
                                            Selected: {selectedFiles[docName].name}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <p>No specific eligibility documents are required for this service.</p>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '1rem' }}>
                        <button type="button" onClick={() => navigate(`/apply/${serviceId}?caseId=${caseId}&step=form`)} className="submit-button document-submit-button">
                            Previous
                        </button>
                        <button
                            onClick={handleSubmitDocuments}
                            disabled={isSubmitting || !allFilesSelected}
                            className="submit-button document-submit-button"
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit All Documents'}
                        </button>
                    </div>
                </div>
            ) : (
                <div style={{ padding: '20px', border: '1px solid #28a745', borderRadius: '5px', backgroundColor: '#e9f7ef' }}>
                    <p style={{ color: '#28a745', fontWeight: 'bold' }}>
                        <FileText size={20} style={{ verticalAlign: 'middle', marginRight: '10px' }} />
                        Documents submitted successfully!
                    </p>

                    <button
                        onClick={handleViewFinalDocuments}
                        style={{ marginTop: '20px', padding: '10px 20px', backgroundColor: hasFinalDocuments ? '#007bff' : '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        {hasFinalDocuments ? 'View Final Document(s) & Finish' : 'Finish Application'}
                    </button>
                </div>
            )}

            {hasFinalDocuments && caseId && currentDocument && (
                <DocumentViewer
                    isOpen={isViewerOpen}
                    onClose={() => setIsViewerOpen(false)}
                    CaseID={caseId}
                    documentType={currentDocument.scenario}
                    documentName={currentDocument.name}
                    onNext={handleNextDocument}
                    hasNext={currentDocumentIndex < finalDocuments.length - 1}
                    onFinishProcess={currentDocumentIndex === finalDocuments.length - 1 ? handleFinishApplication : undefined} // Pass only for the last document
                />
            )}
        </div>
    );
}

export default DocumentUploadPage;
